import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getSocieteInfos, COLORS, getDocumentStyles } from "./exportUtils";
import { formatCurrencyForExport, formatNumberForExport } from "./exportFormatters";

/**
 * Génère un PDF élégant pour le rapport CA
 */
export const generateCAPDF = (data, selectedYear) => {
  return new Promise((resolve, reject) => {
    try {
      const societe = getSocieteInfos();
      const styles = getDocumentStyles();
      
      const mois = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      
      // Création du document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Police par défaut
      doc.setFont('helvetica');
      
      // === EN-TÊTE ===
      doc.setFontSize(24);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text(societe.nom, 105, 20, { align: 'center' });
      
      doc.setFontSize(16);
      doc.setTextColor(COLORS.secondary);
      doc.text(`RAPPORT FINANCIER ${selectedYear}`, 105, 30, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setTextColor(COLORS.dark);
      doc.setFont(undefined, 'normal');
      doc.text(societe.adresse, 105, 38, { align: 'center' });
      doc.text(`Tél: ${societe.telephone} | Email: ${societe.email}`, 105, 44, { align: 'center' });
      
      // Ligne de séparation
      doc.setDrawColor(COLORS.light);
      doc.setLineWidth(0.5);
      doc.line(20, 50, 190, 50);
      
      // === RÉSUMÉ RAPIDE ===
      doc.setFontSize(11);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text(`Résumé financier`, 20, 60);
      
      doc.setFontSize(10);
      doc.setTextColor(COLORS.dark);
      doc.setFont(undefined, 'normal');
      doc.text(`CA Annuel: ${formatCurrencyForExport(data.totaux?.ca_annuel || 0)} FCFA`, 20, 70);
      doc.text(`CA Mensuel: ${formatCurrencyForExport(data.totaux?.ca_mensuel || 0)} FCFA`, 20, 77);
      doc.text(`Nombre de clients: ${formatNumberForExport(data.totaux?.nb_clients || 0)}`, 20, 84);
      doc.text(`Créances totales: ${formatCurrencyForExport(data.totaux?.creances_total || 0)} FCFA`, 20, 91);
      
      // === TABLEAU MENSUEL ===
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Évolution mensuelle du CA', 20, 105);
      
      // Préparer les données mensuelles
      const mensuelData = [];
      if (data.ca_mensuel && data.ca_mensuel.length > 0) {
        data.ca_mensuel.forEach((montant, index) => {
          if (montant > 0) {
            mensuelData.push([
              mois[index],
              formatCurrencyForExport(montant) + ' FCFA'
            ]);
          }
        });
      }
      
      autoTable(doc, {
        startY: 110,
        head: [['Mois', 'Chiffre d\'affaires']],
        body: mensuelData,
        theme: 'striped',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: COLORS.primary,
          textColor: COLORS.white,
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === TOP PRODUITS ===
      const lastY = doc.lastAutoTable.finalY + 15;
      
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Top produits', 20, lastY);
      
      const produitsData = data.top_produits?.map(produit => [
        produit.designation || '-',
        produit.reference || '-',
        formatNumberForExport(parseInt(produit.quantite_vendue) || 0),
        formatCurrencyForExport(parseFloat(produit.montant_total) || 0) + ' FCFA'
      ]) || [];
      
      autoTable(doc, {
        startY: lastY + 5,
        head: [['Désignation', 'Référence', 'Quantité', 'Montant']],
        body: produitsData,
        theme: 'striped',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: COLORS.primary,
          textColor: COLORS.white,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === TOP CLIENTS ===
      const clientsY = doc.lastAutoTable.finalY + 15;
      
      let clientsStartY;
      if (clientsY > 250) {
        doc.addPage();
        clientsStartY = 20;
      } else {
        clientsStartY = clientsY;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Top clients', 20, clientsStartY);
      
      const clientsData = data.top_clients?.map(client => [
        client.raison_sociale || '-',
        client.code_client || '-',
        formatNumberForExport(client.nb_factures || 0),
        formatCurrencyForExport(parseFloat(client.total_achats) || 0) + ' FCFA'
      ]) || [];
      
      autoTable(doc, {
        startY: clientsStartY + 5,
        head: [['Client', 'Code', 'Factures', 'Total achats']],
        body: clientsData,
        theme: 'striped',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: COLORS.primary,
          textColor: COLORS.white,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === VENTES PAR CATÉGORIE ===
      const catY = doc.lastAutoTable.finalY + 15;
      
      let catStartY;
      if (catY > 250) {
        doc.addPage();
        catStartY = 20;
      } else {
        catStartY = catY;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.setFont(undefined, 'bold');
      doc.text('Ventes par catégorie', 20, catStartY);
      
      const catData = data.ventes_categories?.map(cat => [
        cat.nom_categorie || 'Sans catégorie',
        formatCurrencyForExport(parseFloat(cat.montant_total) || 0) + ' FCFA'
      ]) || [];
      
      autoTable(doc, {
        startY: catStartY + 5,
        head: [['Catégorie', 'Montant']],
        body: catData,
        theme: 'striped',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: COLORS.primary,
          textColor: COLORS.white,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      // === PIED DE PAGE ===
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(COLORS.secondary);
        doc.setFont(undefined, 'italic');
        
        const dateGen = new Date().toLocaleDateString('fr-FR');
        doc.text(`Document généré le ${dateGen}`, 20, 285);
        doc.text(`Page ${i} / ${totalPages}`, 180, 285, { align: 'right' });
      }
      
      const fileName = `Rapport_financier_${selectedYear}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      resolve(true);
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      reject(error);
    }
  });
};